import React from 'react';
import { Link } from 'gatsby';
import {
  AcademicCapIcon,
  UsersIcon,
  CollectionIcon
} from '@heroicons/react/outline';
import Button from './common/Button';

interface FeatureBlockProps {
  title?: string;
  body?:
    | string
    | {
        featureBlock1Body?: string;
        featureBlock2Body?: string;
        featureBlock3Body?: string;
      };
  ctaButtonText?: string;
  ctaButtonLink?: string;
  position?: string;
  images?: Array<{
    fluid: any;
  }>;
  index?: number;
  blockNumber?: number;
}

const FeatureBlock: React.FC<FeatureBlockProps> = ({
  title,
  body,
  ctaButtonText,
  ctaButtonLink,
  position,
  images,
  index = 0,
  blockNumber = 1
}) => {
  // Extract body text - handle both string and nested object formats
  const bodyText =
    typeof body === 'string'
      ? body
      : body?.featureBlock1Body ||
        body?.featureBlock2Body ||
        body?.featureBlock3Body ||
        '';

  // Don't render if no content
  if (!title && !bodyText && !ctaButtonText) {
    return null;
  }

  // Determine icon based on block number
  const getIcon = () => {
    switch (blockNumber) {
      case 1:
        return AcademicCapIcon;
      case 2:
        return UsersIcon;
      case 3:
        return CollectionIcon;
      default:
        return AcademicCapIcon;
    }
  };

  const IconComponent = getIcon();

  // Determine if content should be on left or right
  // Position from Contentful takes precedence, otherwise use index
  const isLeft =
    position?.toLowerCase() === 'left' ||
    (position?.toLowerCase() !== 'right' && index % 2 === 0);

  const hasImages = images && images.length > 0;
  const imageSrcs =
    images?.map((img: any) => img.fluid?.src).filter(Boolean) || [];
  const presentImages = imageSrcs;

  return (
    <section
      className={index % 2 === 0 ? 'py-8 bg-white' : 'py-12 pattern-dots'}
    >
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          <div
            className={`grid ${hasImages ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-8 items-center`}
          >
            {isLeft ? (
              <div className="magazine-card bg-white creative-shadow border border-primary/10 p-8 lg:p-10">
                {title && (
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-primary">
                      {title}
                    </h2>
                  </div>
                )}
                {bodyText && (
                  <div
                    className="text-foreground/80 text-base leading-relaxed mb-6"
                    dangerouslySetInnerHTML={{ __html: bodyText }}
                  />
                )}
                {ctaButtonText && ctaButtonLink && (
                  <Link to={ctaButtonLink}>
                    <Button
                      className="bg-primary text-white hover:bg-primary/90"
                      text={ctaButtonText}
                    />
                  </Link>
                )}
              </div>
            ) : null}
            {hasImages ? (
              <div
                className={
                  presentImages.length === 1
                    ? 'flex justify-center'
                    : 'grid grid-cols-2 gap-4'
                }
              >
                {presentImages.map((src: string, i: number) => (
                  <img
                    key={i}
                    src={src}
                    alt={`${title || 'Feature'} image ${i + 1}`}
                    className={
                      presentImages.length === 1
                        ? 'w-full h-full object-cover rounded-xl shadow-lg'
                        : i === 1
                          ? 'w-full h-full object-cover rounded-xl shadow-lg mt-8'
                          : 'w-full h-full object-cover rounded-xl shadow-lg'
                    }
                  />
                ))}
              </div>
            ) : null}
            {!isLeft ? (
              <div className="magazine-card bg-white creative-shadow p-8 lg:p-10">
                {title && (
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-primary">
                      {title}
                    </h2>
                  </div>
                )}
                {bodyText && (
                  <div
                    className="text-foreground/80 text-base leading-relaxed mb-6"
                    dangerouslySetInnerHTML={{ __html: bodyText }}
                  />
                )}
                {ctaButtonText && ctaButtonLink && (
                  <Link to={ctaButtonLink}>
                    <Button
                      className="bg-transparent border-primary hover:bg-primary hover:text-white"
                      text={ctaButtonText}
                    />
                  </Link>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureBlock;
